import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import { Menu } from '@dropbox/dig-components/menu';
import { IconButton } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import {
  CustomizeLine,
  MoreVerticalLine,
  PersonLeaveLine,
  FailLine,
  DownloadLine,
  CloseLine,
} from '@dropbox/dig-icons/assets';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import classnames from 'classnames';
import messages from 'signer-app/signer-experience/signer-toolbar/messages';
import styles from 'signer-app/signer-experience/signer-toolbar/toolbar-menu.module.css';
import * as embeddedActions from 'signer/actions/embedded';
import { SignatureRequest } from 'signer-app/signer-signature-document/types';
import { CookiesAndCcpaFeatureGate } from 'hello-react/components/cookies-and-ccpa-feature';
import { useBundleProps } from '../../bundle-props';

interface Props {
  isLarge: boolean;
  onReassign: () => void;
  onDecline: () => void;
}

export function ToolbarMenu({ isLarge, onReassign, onDecline }: Props) {
  const intl = useIntl();
  const { signatureRequestId } = useSignerAppParams();
  const { hasCustomStyles } = useBundleProps();

  const {
    embeddedParentUrl,
    signatureRequest,
    allowCancel: allowClose,
    allowReassign,
    allowDecline,
    isMobileReady,
    hideFormViewToggle,
  } = useSignerContext();

  const isEmbedded = !!embeddedParentUrl;
  const { step } = useSignerAppParams();

  const menuVariant =
    isMobileReady && !hideFormViewToggle && step === 'preview'
      ? 'opacity'
      : 'borderless';

  const isWhiteLabeled = hasCustomStyles && step !== 'preview';

  const allowDownload =
    !isEmbedded && !anyFieldsEdited(signatureRequest.fields);

  const allowCookie = !isEmbedded;

  const hasMenuOptions =
    allowReassign ||
    allowDownload ||
    allowDecline ||
    allowCookie ||
    !!allowClose;

  if (!hasMenuOptions) {
    return null;
  }

  return (
    <Menu.Wrapper onSelection={() => {}} data-testid="menu-toggle">
      {({ getTriggerProps, getContentProps }) => {
        const triggerProps = getTriggerProps();
        return (
          <>
            <IconButton
              {...triggerProps}
              variant={menuVariant}
              size={isLarge ? 'large' : 'medium'}
              className={classnames(
                triggerProps.className,
                isWhiteLabeled && styles.menuTriggerNoHover,
              )}
            >
              <UIIcon
                src={MoreVerticalLine}
                aria-label={intl.formatMessage(messages.menu)}
                className={classnames(
                  isWhiteLabeled && styles.menuTriggerIconWhiteLabel,
                )}
              />
            </IconButton>
            <WhiteLabelThemeProvider colors="reset">
              <Menu.Content
                {...getContentProps()}
                data-testid="signer-toolbar-menu"
              >
                {allowReassign && (
                  <Menu.ActionItem
                    value="reassign"
                    withLeftAccessory={
                      <UIIcon src={PersonLeaveLine} role="presentation" />
                    }
                    onClick={onReassign}
                  >
                    <FormattedMessage
                      id="8f24b2a0b3d67ed7d3118cc58510ff916cc0f8714700314e439c36978448335d"
                      description="link text in signer flow for signer to request another user to sign instead"
                      defaultMessage="Reassign"
                    />
                  </Menu.ActionItem>
                )}
                {allowDecline ? (
                  <Menu.ActionItem
                    onClick={onDecline}
                    value="decline"
                    withLeftAccessory={
                      <UIIcon src={FailLine} role="presentation" />
                    }
                  >
                    {intl.formatMessage(messages.decline)}
                  </Menu.ActionItem>
                ) : null}
                {allowDownload ? (
                  <Menu.LinkItem
                    href={`/attachment/downloadCopy?o=1&guid=${signatureRequestId}`}
                    key={'download'}
                    withLeftAccessory={
                      <UIIcon src={DownloadLine} role="presentation" />
                    }
                  >
                    {intl.formatMessage(messages.download)}
                  </Menu.LinkItem>
                ) : null}
                {allowClose ? (
                  <Menu.ActionItem
                    key="close"
                    value="close"
                    onClick={() =>
                      embeddedActions.closeWindow({
                        embeddedData: {
                          parentUrl: embeddedParentUrl,
                        },
                      })
                    }
                    withLeftAccessory={
                      <UIIcon src={CloseLine} role="presentation" />
                    }
                  >
                    <FormattedMessage
                      id="signer.headerMenu.close"
                      description="link text in signer flow, closes the signer flow for signer"
                      defaultMessage="Close"
                    />
                  </Menu.ActionItem>
                ) : null}

                {allowCookie ? (
                  <CookiesAndCcpaFeatureGate>
                    <Menu.ActionItem
                      data-testid="global-header-cookies-link"
                      onClick={() => {
                        window.dropbox?.privacyConsent?.openOptionsDialog?.();
                      }}
                      withLeftAccessory={<UIIcon src={CustomizeLine} />}
                    >
                      <FormattedMessage
                        id="f41bd7c540647a5f0bcf527df7ff9fd4c8c629413ba3709c22b4294891be137b"
                        description="Text for a button which displays cookie preferences when pressed. CCPA stands for California Consumer Privacy Act"
                        defaultMessage="Cookies & CCPA preferences"
                      />
                    </Menu.ActionItem>
                  </CookiesAndCcpaFeatureGate>
                ) : null}
              </Menu.Content>
            </WhiteLabelThemeProvider>
          </>
        );
      }}
    </Menu.Wrapper>
  );
}

function anyFieldsEdited(fields: SignatureRequest['fields']) {
  return fields.some((field) => {
    if (field.readOnly) {
      return false;
    } else if (field.type === 'checkbox' || field.type === 'radiobutton') {
      return Boolean(field.checked);
    } else if (field.type === 'initials' || field.type === 'signature') {
      return Boolean(field.signature);
    } else if (field.type === 'dropdown') {
      return Boolean(field.value);
    } else if (field.type === 'text') {
      return field.isAutoFill
        ? field.autoFillValue !== field.value
        : field.value && field.value.replace(/[\n\r]/g, '').length > 0;
    } else {
      return false;
    }
  });
}
